/* eslint-disable react/button-has-type */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { useHistory } from 'react-router';
import { uploadUserFiles } from '@services/onboarding-service';
import { getBodyForUploadingFiles, getDocSubtitle } from '@helpers/utils';

import Header from '@components/Forms/Header';
import Dropzone from '@components/dropzone/Dropzone';
import Modal from '@components/Modal/ConfirmModal2';
import Button from '@components/Forms/Button';
import Card from '@components/Cards/ScreenCard';
import DocumentCard from '@components/Cards/DocumentCard';

import { hideLoading, showError, getDocumentsTypes } from '@store/common/actions';
import { setToken, setInitialOnboardingState, setMail } from '@store/onboarding/actions';

import { VALIDATION_FORM } from '@constants/routes';
import { onboardinErrorObject } from '@constants/errorObjects';
import { getDocObject } from '@constants/documentation';
import useCommon from '@hooks/useCommon';

import classes from './DocumentForm.module.scss';

import { getEnv } from '@config';

const { publicPath } = getEnv();

const DocumentForm = () => {
  const {
    documents,
    token: stateToken,
    userData,
    commerceData,
  } = useSelector((state) => state.onboarding);
  const { docTypes } = useSelector((state) => state.common);
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { pushNewSentEmail } = useCommon();

  const [fileList, setFileList] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [connectionError, setConnectionError] = useState(false);

  useEffect(() => {
    dispatch(hideLoading());
    dispatch(getDocumentsTypes());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSuccess = async (res) => {
    dispatch(setToken(res.token));

    dispatch(setInitialOnboardingState());
    let email = '';
    if (commerceData.email.length > 0) {
      email = commerceData.email;
    } else {
      email = userData.email;
    }

    pushNewSentEmail({ email, token: res.token });

    dispatch(setMail(email));
    history.push(`${VALIDATION_FORM}`);
  };
  const handleContinuar = () => {
    history.push(`${VALIDATION_FORM}`);
  };

  const onError = (res) => {
    const { token } = res.message;
    dispatch(setToken(token));
  };

  const doOpenModal = () => {
    setModalOpen(true);
  };

  const handleFilesUploaded = (files) => {
    setFileList(files);
  };

  const handleContinue = () => {
    uploadUserFiles({ token: stateToken }, onSuccess, onError);
    // history.push(`${VALIDATION_FORM}`);
  };

  const handleSuccess = (res) => {
    dispatch(setToken(res.token));
    setUploaded(true);
    setUploading(false);
    let email = '';
    if (commerceData.email.length > 0) {
      email = commerceData.email;
    } else {
      email = userData.email;
    }

    pushNewSentEmail({ email, token: res.token });
    dispatch(setMail(email));
  };

  const handleError = (res) => {
    dispatch(showError(onboardinErrorObject(res)));
    setUploading(false);
    if (
      res.message === 'Error de conexion' ||
      res.message?.message === 'Request failed with status code 404'
    ) {
      setConnectionError(true);
    }
  };

  const handleSubir = () => {
    const data = getBodyForUploadingFiles(stateToken, fileList);
    uploadUserFiles(data, handleSuccess, handleError);
    setUploading(true);
  };
  const handleOmitir = () => {
    if (fileList.length > 0) {
      doOpenModal();
    } else {
      uploadUserFiles({ token: stateToken }, onSuccess, onError);
    }
  };

  const handleBack = () => {
    history.goBack();
  };
  return (
    <>
      <div className={classes.DocumentForm}>
        <Header withoutTextMobile withCoockiesAlert />
        <Card title={t('Onboarding.MerchantOnboarding.documentationForm.title')} width="608px">
          {docTypes && (
            <div className={classes.DocumentForm_card_documents}>
              {documents.docTypes.map((it, i) => {
                const doc = getDocObject(it);
                const id = `document-card-${i}`;
                return (
                  <div id={id}>
                    <DocumentCard
                      key={id}
                      title={doc.title}
                      description={getDocSubtitle(doc, 'detailText')}
                    />
                  </div>
                );
              })}
            </div>
          )}
          <Dropzone
            className={classes.dropzone}
            history={history}
            handleFilesUploaded={handleFilesUploaded}
            uploading={uploading}
            success={uploaded}
            delay
            connectionError={connectionError}
          />
          <div className={classes.buttons}>
            <button className={classes.back} onClick={handleBack} id="onb-documentForm-back">
              <img
                src={`${publicPath}/backArrow.svg`}
                alt="Imagen retroceder"
                id="img-back-arrow-document-form"
              />
            </button>
            <Button
              className={classes.skip}
              id="onb-documentForm-skip"
              variant="outlined"
              onClick={handleOmitir}
              tag="skip"
              disabled={uploaded}
            >
              {t('Onboarding.MerchantOnboarding.documentationForm.skipButton')}
            </Button>
            {!uploaded ? (
              <Button
                className={classes.upload}
                id="onb-documentForm-upload"
                onClick={handleSubir}
                tag="send"
                disabled={!(fileList.length > 0) || uploading}
              >
                {t('Onboarding.MerchantOnboarding.documentationForm.uploadButton')}
              </Button>
            ) : (
              <Button
                className={classes.upload}
                id="onb-documentForm-next"
                onClick={handleContinuar}
              >
                {t('Onboarding.MerchantOnboarding.documentationForm.continueButton')}
              </Button>
            )}
          </div>
        </Card>
      </div>
      {modalOpen && (
        <Modal
          title={t('Onboarding.MerchantOnboarding.documentationForm.documentAlertTitle')}
          subTitle={t('Onboarding.MerchantOnboarding.documentationForm.documentAlertSubtitle')}
          image="warning.svg"
          open={modalOpen}
          textConfirm={t('Onboarding.MerchantOnboarding.documentationForm.skipAnywayButton')}
          textCancel={t('Onboarding.MerchantOnboarding.documentationForm.cancelButton')}
          handleCancel={() => {
            setModalOpen(false);
          }}
          handleConfirm={handleContinue}
        />
      )}
    </>
  );
};

export default DocumentForm;
